import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import { Menu } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import ActionMenuItem from "@entrilia/shared/components/ActionMenuItem";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";

import { defined } from "@entrilia/shared/utilities/typeHelper";
import useClientContext from "../../../../../../context/useClientContext";
import useUserContext from "../../../../../../context/useUserContext";
import type { ReportCompany } from "../../../sv/components/viewer/common/sharing/ShareReport.types.ts";
import ShareReportGroup from "../../../sv/components/viewer/common/sharing/ShareReportGroup.tsx";
import { useGroupsContext } from "../contexts/GroupsContext.ts";
import type { GroupType } from "../GroupType";
import DeleteGroupDialog from "./DeleteGroupDialog";
import RenameGroupDialog from "./RenameGroupDialog";

interface Props {
  anchorEl: HTMLElement | null;
  currentGroupId: string;
  showRename?: boolean;
  showDelete?: boolean;
  type: GroupType;
  onClose: () => void;
}

export const GroupActions = (props: Props) => {
  const { anchorEl, currentGroupId, showDelete, showRename, onClose, type } = props;

  const { clientCode } = useClientContext();
  const { groups: reportGroups, actions } = useGroupsContext();
  const { id: currentUserId, clients } = useUserContext();
  const { sendNotification } = useNotificationContext();
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean | undefined>(undefined);
  const [openRenameDialog, setOpenRenameDialog] = useState<boolean | undefined>(undefined);
  const [openShareDialog, setOpenShareDialog] = useState<boolean | undefined>(undefined);
  const [open, setOpen] = useState(() => !!anchorEl);
  const groups = defined(reportGroups[clientCode]);

  const group = useMemo(() => {
    return groups.find((r) => r.id === currentGroupId);
  }, [groups, currentGroupId]);

  const company = useMemo((): ReportCompany | undefined => {
    const client = clients.find((c) => c.clientCode === clientCode);
    return client
      ? { clientCode: client.clientCode, name: client.title, logoMarkUrl: client.branding.logoMarkUrl }
      : undefined;
  }, [clients, clientCode]);

  const canShareGroup =
    (type === "report" || type === "dashboard") && Boolean(actions.getSharing) && Boolean(actions.updateSharing);

  const onDeleteReport = useCallback(() => {
    setOpen(false);
    setOpenDeleteDialog(true);
  }, [setOpenDeleteDialog]);

  const onRenameReport = useCallback(() => {
    setOpen(false);
    setOpenRenameDialog(true);
  }, [setOpenRenameDialog]);

  const onShareGroup = useCallback(() => {
    setOpen(false);
    setOpenShareDialog(true);
  }, []);

  useEffect(() => {
    if (openDeleteDialog === false || openRenameDialog === false || openShareDialog === false) {
      onClose();
    }
  }, [openDeleteDialog, openRenameDialog, openShareDialog, onClose]);

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => onClose()}
        slotProps={{
          paper: {
            sx: { width: "200px" },
          },
        }}
      >
        {showRename && (
          <ActionMenuItem
            key={"rename"}
            icon={<DriveFileRenameOutlineOutlinedIcon />}
            title="Rename Group"
            onClick={onRenameReport}
          />
        )}
        {canShareGroup && (
          <ActionMenuItem key={"share"} icon={<PeopleAltOutlinedIcon />} title="Share Group" onClick={onShareGroup} />
        )}
        {showDelete && (
          <ActionMenuItem
            key={"delete"}
            icon={<DeleteOutlineOutlinedIcon color="error" />}
            title="Delete Group"
            onClick={onDeleteReport}
          />
        )}
      </Menu>
      {openDeleteDialog && group && (
        <DeleteGroupDialog
          group={group}
          onDeleted={() => sendNotification("The group was successfully deleted.")}
          onClose={() => setOpenDeleteDialog(false)}
          type={type}
        />
      )}
      {openRenameDialog && group && (
        <RenameGroupDialog
          group={group}
          onChanged={() => sendNotification("The group was successfully renamed.")}
          onClose={() => setOpenRenameDialog(false)}
        />
      )}
      {openShareDialog && group && company && actions.getSharing && actions.updateSharing && (
        <ShareReportGroup
          clientCode={clientCode}
          groupId={group.id}
          groupCaption={group.caption}
          company={company}
          currentUserId={currentUserId}
          getSharing={actions.getSharing}
          updateSharing={actions.updateSharing}
          onClose={() => setOpenShareDialog(false)}
        />
      )}
    </>
  );
};

export default GroupActions;
